import type { DeskDetail, DeskStage } from '@deskworks/shared-types';
import { useNavigate } from '@tanstack/react-router';
import { GraduationCap, Handshake, Plane } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/** Where a desk stands (docs/features/desk-stages.md). Each stage has its own job; God mode is a switch of its own. */
export const DESK_STAGE_INFO: Record<DeskStage, { label: string; hint: string; icon: ReactNode }> = {
  learning: {
    label: t('Learning'),
    hint: t('The desk learns how the work is done here: the Lead trains it step by step, asks what it needs to know, and writes playbooks and skills.'),
    icon: <GraduationCap />,
  },
  copilot: {
    label: t('Co-Pilot'),
    hint: t('The desk does the work; a person approves every result before the work item closes.'),
    icon: <Handshake />,
  },
  autopilot: {
    label: t('Autopilot'),
    hint: t('The desk works on its own and asks only about what matters: steps that can’t be undone, cases no playbook covers, conflicts, money, legal matters, security.'),
    icon: <Plane />,
  },
};

const STAGES: DeskStage[] = ['learning', 'copilot', 'autopilot'];

/** The desk's stage as a small badge (desk header). */
export function StageBadge({ stage }: { stage: DeskStage }) {
  const info = DESK_STAGE_INFO[stage];
  return (
    <span
      title={info.hint}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium [&_svg]:size-3.5',
        stage === 'learning' && 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-200',
        stage === 'copilot' && 'border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-200',
        stage === 'autopilot' && 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
      )}
    >
      {info.icon} {info.label}
    </span>
  );
}

/** Segmented switch between the desk's stages; Autopilot asks for confirmation first. */
export function StageToggle({ desk }: { desk: DeskDetail }) {
  const save = useApiMutation((stage: DeskStage) => api.desks.update(desk.id, { stage }), {
    invalidate: [qk.desk(desk.id), qk.desks(desk.workspaceId)],
    success: (_, stage) => t('{stage} — {hint}', { stage: DESK_STAGE_INFO[stage].label, hint: DESK_STAGE_INFO[stage].hint }),
  });

  return (
    <div className="inline-flex max-w-full flex-wrap rounded-md border bg-muted/40 p-0.5 text-xs" role="radiogroup" aria-label={t('Stage of this desk')}>
      {STAGES.map((stage) => {
        const info = DESK_STAGE_INFO[stage];
        const active = desk.stage === stage;
        if (stage === 'autopilot' && !active) {
          return (
            <Confirm
              key={stage}
              title={t('Put this desk on Autopilot?')}
              description={
                <>
                  {info.hint}{' '}
                  {t('Results no longer wait for your approval. Tools that ask, approval rules and the guidelines still hold.')}
                </>
              }
              confirmLabel={t('Turn on Autopilot')}
              onConfirm={() => save.mutateAsync(stage)}
            >
              <Segment active={false} title={info.hint}>
                {info.icon} {info.label}
              </Segment>
            </Confirm>
          );
        }
        return (
          <Segment key={stage} active={active} title={info.hint} onClick={() => !active && save.mutate(stage)}>
            {info.icon} {info.label}
          </Segment>
        );
      })}
    </div>
  );
}

/**
 * The desk's training (docs/features/desk-training.md): a chat with its Lead on the Training tab — a link to it while it
 * runs, otherwise a button that opens the tab to start one. In any stage, also on a desk that knows its work already.
 */
export function TrainingNote({ desk }: { desk: DeskDetail }) {
  const navigate = useNavigate();
  const open = () =>
    void navigate({ to: '/w/$workspaceId/desks/$deskId', params: { workspaceId: desk.workspaceId, deskId: desk.id }, search: { tab: 'training' } });
  if (!desk.trainingConversationId) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Button size="sm" variant="outline" onClick={open} disabled={!desk.leadAgent}>
          <GraduationCap /> {t('Start training')}
        </Button>
        {t('You talk with the Lead, and it builds the desk up with you on real cases.')}
      </div>
    );
  }
  const tab = (
    <button type="button" onClick={open} className="cursor-pointer underline hover:text-foreground">
      {t('Training')}
    </button>
  );
  return <p className="text-xs text-muted-foreground">{tr('The Lead learns with you in the {tab}.', { tab })}</p>;
}

/** Forwards the trigger props Confirm (asChild) passes in. */
function Segment({ active, className, children, ...rest }: ComponentProps<'button'> & { active: boolean }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={cn(
        'inline-flex h-6 cursor-pointer items-center gap-1 rounded px-2 font-medium text-muted-foreground transition-colors hover:text-foreground [&_svg]:size-3.5',
        active && 'bg-background text-foreground shadow-sm',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
